import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/store/authStore';
import {
  superAdminsApi,
  SuperAdminFilters,
  InviteSuperAdminPayload,
  UpdateProfilePayload,
  ChangePasswordPayload,
} from '@/api/superadmins';

/**
 * Query key factory for super admins
 */
export const superAdminKeys = {
  all: ['super-admins'] as const,
  lists: () => [...superAdminKeys.all, 'list'] as const,
  list: (filters: SuperAdminFilters) => [...superAdminKeys.lists(), filters] as const,
};

/**
 * Hook to fetch paginated list of super admins
 */
export function useSuperAdmins(filters: SuperAdminFilters) {
  return useQuery({
    queryKey: superAdminKeys.list(filters),
    queryFn: () => superAdminsApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to invite a new super admin
 */
export function useInviteSuperAdmin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: InviteSuperAdminPayload) => superAdminsApi.invite(payload),
    onSuccess: () => {
      // Invalidate super admins list to refetch
      queryClient.invalidateQueries({ queryKey: superAdminKeys.lists() });
      toast({
        title: 'Success',
        description: 'Super admin invitation sent successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to resend invitation to a pending super admin
 */
export function useResendSuperAdminInvite() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (email: string) => superAdminsApi.resendInvite(email),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Invitation resent successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to resend invitation',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update current super admin profile
 */
export function useUpdateSuperAdminProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const loadUser = useAuthStore((state) => state.loadUser);

  return useMutation({
    mutationFn: (payload: UpdateProfilePayload) => superAdminsApi.updateProfile(payload),
    onSuccess: async () => {
      // Invalidate auth data and reload user in Zustand store
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      await loadUser();
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update profile',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to change current super admin password
 */
export function useChangeSuperAdminPassword() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) => superAdminsApi.changePassword(payload),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to change password',
        variant: 'destructive',
      });
    },
  });
}

// ==================== Manage Other Super Admins ====================

/**
 * Hook to update another super admin's profile
 */
export function useUpdateAdmin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProfilePayload }) =>
      superAdminsApi.updateAdmin(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.lists() });
      toast({
        title: 'Success',
        description: 'Super admin updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update super admin',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to deactivate another super admin
 */
export function useDeactivateAdmin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => superAdminsApi.deactivateAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.lists() });
      toast({
        title: 'Success',
        description: 'Super admin deactivated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to deactivate super admin',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to reset 2FA for another super admin
 */
export function useReset2FA() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => superAdminsApi.reset2FA(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.lists() });
      toast({
        title: 'Success',
        description: '2FA has been reset. Admin will need to set up 2FA again.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to reset 2FA',
        variant: 'destructive',
      });
    },
  });
}
